import { useState, useEffect, useCallback } from 'react';
import { fetchInvoices, publishInvoice, type InvoiceApiRecord, type InvoiceStatus, type InvoiceListMeta } from '../../services/invoiceApi';
import { getAcademicSessionId } from '../../services/auth';
import InvoiceDetail from './InvoiceDetail';
import './StudentInvoices.css';

const STATUS_LABELS: Record<InvoiceStatus, string> = {
    paid: 'Paid', partial: 'Partial', overdue: 'Overdue', published: 'Published', draft: 'Draft', void: 'Void',
};

const PAGE_SIZE = 15;

function studentLabel(student: InvoiceApiRecord['student']): string {
    return student?.name ?? student?.enrollment_number ?? student?.id ?? 'Unknown student';
}

function formatDate(iso: string): string {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function StudentInvoices() {
    const [invoices, setInvoices] = useState<InvoiceApiRecord[]>([]);
    const [meta, setMeta] = useState<InvoiceListMeta>({ current_page: 1, last_page: 1, per_page: PAGE_SIZE, total: 0 });
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    /* ── Draft filter state ── */
    const [searchInput, setSearchInput] = useState('');
    const [statusInput, setStatusInput] = useState<InvoiceStatus | ''>('');

    /* ── Applied filter state ── */
    const [appliedSearch, setAppliedSearch] = useState('');
    const [appliedStatus, setAppliedStatus] = useState<InvoiceStatus | ''>('');

    const [page, setPage] = useState(1);

    /* ── Detail view ── */
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

    /* ── Modals ── */
    const [publishAllModal, setPublishAllModal] = useState(false);
    const [publishSingleModal, setPublishSingleModal] = useState<InvoiceApiRecord | null>(null);
    const [isPublishing, setIsPublishing] = useState(false);

    /* ── Load invoices ── */
    const loadInvoices = useCallback(async () => {
        setLoading(true);
        setApiError('');
        try {
            const res = await fetchInvoices({
                academic_session_id: getAcademicSessionId() ?? undefined,
                status: appliedStatus || undefined,
                per_page: PAGE_SIZE,
                page,
            });
            setInvoices(res.data);
            setMeta(res.meta);
        } catch (err) {
            setApiError((err as { message?: string })?.message ?? 'Failed to load invoices.');
        } finally {
            setLoading(false);
        }
    }, [appliedStatus, page]);

    useEffect(() => { loadInvoices(); }, [loadInvoices]);

    /* Reset to page 1 when status filter changes */
    useEffect(() => { setPage(1); }, [appliedStatus]);

    /* ── Client-side search on current page ── */
    const filtered = appliedSearch
        ? invoices.filter((inv) => {
            const q = appliedSearch.toLowerCase();
            return (
                inv.invoice_number.toLowerCase().includes(q) ||
                (inv.student?.name ?? '').toLowerCase().includes(q) ||
                (inv.student?.enrollment_number ?? '').toLowerCase().includes(q) ||
                inv.title.toLowerCase().includes(q)
            );
        })
        : invoices;

    /* Pagination is server-side; when searching, show all filtered results */
    const paged = filtered;
    const showingFrom = meta.total > 0 ? (meta.current_page - 1) * meta.per_page + 1 : 0;
    const showingTo = Math.min(meta.current_page * meta.per_page, meta.total);

    /* ── Search / Clear ── */
    const handleSearch = () => {
        setAppliedSearch(searchInput);
        setAppliedStatus(statusInput);
        setPage(1);
    };

    const handleClear = () => {
        setSearchInput(''); setStatusInput('');
        setAppliedSearch(''); setAppliedStatus('');
        setPage(1);
    };

    const hasActiveFilters = appliedSearch || appliedStatus;

    /* ── Publish single ── */
    const handlePublishSingle = async () => {
        if (!publishSingleModal) return;
        setIsPublishing(true);
        try {
            await publishInvoice(publishSingleModal.id);
            setPublishSingleModal(null);
            showSuccess(`${publishSingleModal.invoice_number} published.`);
            loadInvoices();
        } catch {
            setApiError('Failed to publish invoice.');
        } finally {
            setIsPublishing(false);
        }
    };

    /* ── Publish all drafts ── */
    const handlePublishAll = async () => {
        const drafts = invoices.filter((i) => i.status === 'draft');
        if (drafts.length === 0) {
            setPublishAllModal(false);
            return;
        }
        setIsPublishing(true);
        try {
            const results = await Promise.allSettled(drafts.map((inv) => publishInvoice(inv.id)));
            const fulfilled = results.filter((r) => r.status === 'fulfilled').length;
            const rejected = results.length - fulfilled;
            setPublishAllModal(false);
            if (rejected === 0) {
                showSuccess(`${fulfilled} draft invoice${fulfilled !== 1 ? 's' : ''} published.`);
            } else if (fulfilled === 0) {
                setApiError('Failed to publish drafts.');
            } else {
                showSuccess(`${fulfilled} published, ${rejected} failed.`);
                setApiError(`${rejected} draft invoice${rejected !== 1 ? 's' : ''} failed to publish.`);
            }
            loadInvoices();
        } finally {
            setIsPublishing(false);
        }
    };

    const showSuccess = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 5000);
    };

    /* ── Pagination helpers ── */
    const pageNumbers = (): number[] => {
        const pages: number[] = [];
        const start = Math.max(1, meta.current_page - 2);
        const end = Math.min(meta.last_page, meta.current_page + 2);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    const draftCount = invoices.filter((i) => i.status === 'draft').length;

    /* ── Listing view ── */
    return (
        <div className="si">
            {/* ── Error Banner ── */}
            {apiError && (
                <div className="si__error-banner" role="alert">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" /><line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="16" r="1" fill="currentColor" /></svg>
                    <span>{apiError}</span>
                    <button className="si__error-dismiss" onClick={() => setApiError('')}>×</button>
                </div>
            )}

            {/* ── Toast ── */}
            {successMsg && (
                <div className="si__toast">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="si__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* ── Toolbar ── */}
            <div className="si__toolbar">
                <div className="si__toolbar-left">
                    <div className="si__search-wrapper">
                        <svg className="si__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        <input
                            className="si__search"
                            type="text"
                            placeholder="Search student or invoice ID…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        {searchInput && (
                            <button className="si__search-clear" onClick={() => setSearchInput('')} aria-label="Clear search">×</button>
                        )}
                    </div>

                    <select className="si__filter" value={statusInput} onChange={(e) => setStatusInput(e.target.value as InvoiceStatus | '')}>
                        <option value="">All Status</option>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                    </select>

                    <button className="si__search-btn" onClick={handleSearch}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        Search
                    </button>

                    {hasActiveFilters && (
                        <button className="si__clear-btn" onClick={handleClear} title="Clear all filters">Clear</button>
                    )}
                </div>

                <div className="si__toolbar-right">
                    <span className="si__record-count">{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</span>
                    {draftCount > 0 && (
                        <button className="si__publish-all-btn" onClick={() => setPublishAllModal(true)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /></svg>
                            Publish All Drafts ({draftCount})
                        </button>
                    )}
                </div>
            </div>

            {/* ── Data Grid ── */}
            <div className="si__table-wrap">
                <table className="si__table">
                    <thead>
                        <tr>
                            <th className="si__th">Invoice No.</th>
                            <th className="si__th">Student</th>
                            <th className="si__th">Issue Date</th>
                            <th className="si__th">Due Date</th>
                            <th className="si__th si__th--amount">Net Amount</th>
                            <th className="si__th si__th--center">Status</th>
                            <th className="si__th si__th--actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={7} className="si__loading-cell">
                                <div className="si__loader"><span className="si__loader-dot" /><span className="si__loader-dot" /><span className="si__loader-dot" /></div>
                            </td></tr>
                        )}

                        {!loading && paged.length === 0 && (
                            <tr><td colSpan={7} className="si__empty-cell">
                                <div className="si__empty">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.35"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                                    <p>{hasActiveFilters ? 'No invoices match your criteria.' : 'No invoices found.'}</p>
                                </div>
                            </td></tr>
                        )}

                        {!loading && paged.map((inv) => (
                            <tr className="si__row" key={inv.id}>
                                <td className="si__td si__td--inv">
                                    <button className="si__inv-link" onClick={() => setSelectedInvoiceId(inv.id)}>{inv.invoice_number}</button>
                                </td>
                                <td className="si__td si__td--student">
                                    <span className="si__student-name">{inv.student?.name ?? <span className="si__muted">—</span>}</span>
                                    {inv.student?.enrollment_number && (
                                        <code className="si__student-enroll">{inv.student.enrollment_number}</code>
                                    )}
                                </td>
                                <td className="si__td si__td--date">{formatDate(inv.issue_date)}</td>
                                <td className="si__td si__td--date">{inv.due_date ? formatDate(inv.due_date) : <span className="si__muted">—</span>}</td>
                                <td className="si__td si__td--amount">{formatCurrency(parseFloat(inv.total_amount))}</td>
                                <td className="si__td si__td--center">
                                    <span className={`si__badge si__badge--${inv.status}`}>{STATUS_LABELS[inv.status]}</span>
                                </td>
                                <td className="si__td si__td--actions">
                                    <button className="si__row-btn" title="View / Edit" onClick={() => setSelectedInvoiceId(inv.id)}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                    </button>
                                    {inv.status === 'draft' && (
                                        <button className="si__row-btn si__row-btn--publish" title="Publish" onClick={() => setPublishSingleModal(inv)}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /></svg>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ── Pagination ── */}
            {!loading && meta.total > 0 && !appliedSearch && meta.last_page > 1 && (
                <div className="si__pagination">
                    <div className="si__pagination-left">
                        <span className="si__page-info">Showing {showingFrom}–{showingTo} of {meta.total}</span>
                    </div>
                    <div className="si__pagination-right">
                        <button className="si__page-btn" disabled={page <= 1} onClick={() => setPage(1)} title="First page">«</button>
                        <button className="si__page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} title="Previous page">‹</button>
                        {pageNumbers().map((n) => (
                            <button key={n} className={`si__page-btn ${n === page ? 'si__page-btn--active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                        ))}
                        <button className="si__page-btn" disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)} title="Next page">›</button>
                        <button className="si__page-btn" disabled={page >= meta.last_page} onClick={() => setPage(meta.last_page)} title="Last page">»</button>
                    </div>
                </div>
            )}

            {/* ── Publish All Confirmation Modal ── */}
            {publishAllModal && (
                <>
                    <div className="si__modal-backdrop" onClick={() => !isPublishing && setPublishAllModal(false)} />
                    <div className="si__modal" role="dialog">
                        <h3 className="si__modal-title">Publish All Drafts</h3>
                        <p className="si__modal-text">
                            Are you sure you want to publish <strong>{draftCount}</strong> draft invoice{draftCount !== 1 ? 's' : ''}? This action will make them visible to students and cannot be easily undone.
                        </p>
                        <div className="si__modal-actions">
                            <button className="si__modal-btn si__modal-btn--cancel" onClick={() => setPublishAllModal(false)} disabled={isPublishing}>Cancel</button>
                            <button className="si__modal-btn si__modal-btn--confirm" onClick={handlePublishAll} disabled={isPublishing}>
                                {isPublishing ? 'Publishing…' : 'Publish All'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* ── Publish Single Confirmation Modal ── */}
            {publishSingleModal && (
                <>
                    <div className="si__modal-backdrop" onClick={() => !isPublishing && setPublishSingleModal(null)} />
                    <div className="si__modal" role="dialog">
                        <h3 className="si__modal-title">Publish Invoice</h3>
                        <p className="si__modal-text">
                            Publish <strong>{publishSingleModal.invoice_number}</strong> for <strong>{studentLabel(publishSingleModal.student)}</strong>?
                        </p>
                        <div className="si__modal-actions">
                            <button className="si__modal-btn si__modal-btn--cancel" onClick={() => setPublishSingleModal(null)} disabled={isPublishing}>Cancel</button>
                            <button className="si__modal-btn si__modal-btn--confirm" onClick={handlePublishSingle} disabled={isPublishing}>
                                {isPublishing ? 'Publishing…' : 'Publish'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* ── Invoice Detail Drawer ── */}
            {selectedInvoiceId && (
                <InvoiceDetail
                    invoiceId={selectedInvoiceId}
                    onClose={(changed: boolean) => { setSelectedInvoiceId(null); if (changed) loadInvoices(); }}
                />
            )}
        </div>
    );
}
